import React, { use, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { useAtom } from "jotai";
import { authAtom } from "../../states/auth";
import { Button } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Image from "react-bootstrap/Image";
import {
  DownloadIcon,
  EditIcon,
  PlusIcon,
  SignOutIcon,
  VideoCameraPlusIcon,
  VideoCameraIcon,
} from "../../components/SVGIcon";
import Tabs from "../../components/Tabs/Tabs";
import { endConversationRequest } from "../../services/supportService";
import { useAlert } from "../../contexts/AlertContext";
import { getChatFiles } from "../../services/chatServices";
import { createCall } from "../../services/callServices";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ImagePreviewLightbox from "../../components/ImagePreviewLightbox";
import VideoPopupLightbox from "../../components/VideoPopupLightbox";
import { vonageAccessToken } from "../../services/vonageServices";
import UserStatusText from "../../components/anotherUserStatus";
import { useUserStatus } from "../../components/liveStatus";
import { singleUser } from "../../services/userService";

const downloadAll = async (fileUrls = [], setProgress, id) => {
  if (!fileUrls.length) return;

  // 🟢 Case: Single file – download directly
  if (fileUrls.length === 1) {
    const url = fileUrls[0];
    const filename = decodeURIComponent(url.split("/").pop().split("?")[0]);

    try {
      setProgress?.(10);
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      setProgress?.(100);
      saveAs(blob, filename);
    } catch (error) {
      console.error(`Failed to download ${filename}:`, error);
      setProgress?.(0);
    }
    return;
  }

  // 🟡 Case: Multiple files – fetch in parallel and zip
  const zip = new JSZip();

  try {
    setProgress?.(10);

    // Fetch all files in parallel
    const fileFetchPromises = fileUrls.map(async (url) => {
      const filename = decodeURIComponent(url.split("/").pop().split("?")[0]);

      try {
        const response = await fetch(url, { method: "GET", mode: "cors" });
        const blob = await response.blob();
        return { filename, blob };
      } catch (error) {
        console.error(`Failed to fetch ${filename}:`, error);
        return null;
      }
    });

    const fileBlobs = await Promise.all(fileFetchPromises);

    fileBlobs.forEach((file) => {
      if (file) zip.file(file.filename, file.blob);
    });

    // Update progress before starting zip
    setProgress?.(60);

    const zipBlob = await zip.generateAsync({ type: "blob" }, (metadata) => {
      const percent = Math.floor(60 + metadata.percent * 0.4); // zip phase: 60–100%
      setProgress?.(percent);
    });

    saveAs(zipBlob, `${id}.zip`);
    setProgress?.(100);
  } catch (zipError) {
    console.error("Error generating zip:", zipError);
    setProgress?.(0);
  }
};

const getHourMinuteDiffString = (date1, date2) => {
  const msDiff = Math.abs(date2 - date1);
  const totalMinutes = Math.floor(msDiff / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

const formatDateTimeToString = (dateObjTmp) => {
  const dateObj = new Date(dateObjTmp);
  if (dateObj) {
    const formattedDate = dateObj.toLocaleDateString("en-GB"); // Gives "dd/mm/yyyy"
    const formattedTime = dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // ensures AM/PM format
    });
    const formattedDateWithDashes = formattedDate?.replace(/\//g, "-"); // converts dd/mm/yyyy to dd-mm-yyyy
    return `${formattedDateWithDashes}, ${formattedTime}`;
  }
};

const getAddress = (userInfo) => {
  const addressArr = [];
  if (userInfo?.createdBy?.address) {
    addressArr.push(userInfo?.createdBy?.address);
  }
  if (userInfo?.createdBy?.city) {
    addressArr.push(userInfo?.createdBy?.city);
  }
  if (userInfo?.createdBy?.state?.name) {
    addressArr.push(userInfo?.createdBy?.state?.name);
  }
  if (userInfo?.createdBy?.country?.code) {
    addressArr.push(userInfo?.createdBy?.country?.code);
  }
  if (userInfo?.createdBy?.zipCode) {
    addressArr.push(userInfo?.createdBy?.zipCode);
  }
  return addressArr.join(", ");
};

const formatDate = (date) => {
  if (!date) return "From - To Date";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

function evaluateSessionTiming(startDateUTC, endDateUTC, isChat) {
  if (isChat) return { isFutureSession: false, isPastSession: false, isSessionInWhile: false, isCurrentSession: false };

  const now = new Date();
  const utcNow = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );

  const start = new Date(startDateUTC).getTime();
  const end = new Date(endDateUTC).getTime();

  return {
    isFutureSession: start > utcNow,
    isPastSession: end < utcNow,
    isCurrentSession: start <= utcNow && utcNow < end,
    isSessionInWhile: start > utcNow && start - utcNow <= 600000,
  };
}

const ChatHeader = ({
  conversationData,
  chatScreenFilter,
  setChatScreenFilter,
  meetingId,
  setMeetingId,
}) => {
  const [activeTab, setActiveTab] = useState("docs");
  const [chatFiles, setChatFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userDetailModal, setUserDetailModal] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(null);
  const [exitGroupModal, setExitGroupModal] = useState(false);
  const [makeAdminModal, setMakeAdminModal] = useState(false);
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [progress, setProgress] = useState("");
  const [editGroup, setEditGroup] = useState(false);
  const [addUser, setAddUser] = useState(false);
  const [groupUserInfo, setGroupUseInfo] = useState(false);
  const { showAlert } = useAlert();
  const [anotherUser, setAnotherUser] = useState({});
  const [tick, setTick] = useState(0);
  const [tabVisible, setTabVisible] = useState(!document.hidden);
  const [filesPagination, setFilesPagination] = useState({
    limit: 100,
    nextToken: null,
  });
  const [hasMore, setHasMore] = useState(true);
  const [auth] = useAtom(authAtom);

  const navigate = useNavigate();
  const chatType = conversationData?.conversationType?.includes("chat")
    ? true
    : false;
  const anotherMember = conversationData?.conversationMembers?.find((e) => e.user_id != auth?.user?.id);

  const sessionTimeStatus = useMemo(
    () => evaluateSessionTiming(
      conversationData.startTime,
      conversationData.endTime,
      chatType
    ),
    [conversationData.startTime, conversationData.endTime, chatType, tick]
  );
  console.log("conversationData", conversationData);

  useEffect(() => {
    setHasMore(true);
    // setChatFiles([]);
    if (conversationData?.id && userDetailModal) {
      getFiles();
    } else {
      // setChatFiles([])
    }
    setFilesPagination({ limit: 100, nextToken: null });
  }, [conversationData, userDetailModal]);
  useEffect(() => {

    singleUser(anotherMember?.user_id).then((response) => {
      if (response?.data?.user) {
        const tempUserData = response?.data?.user;
        setAnotherUser(tempUserData);
      }
    })
  }, [anotherMember?.user_id]);

  const meetingLinkOpen = (event, callId) => {
    event.preventDefault();
    try {
      console.log("asdasd", auth.user.firstName);
      const callRequest = {
        roomName: callId,
        identifierName: auth?.user?.firstName + auth?.user?.lastName,
      };

      const data = vonageAccessToken(callRequest).then((resp) => {
        console.log("response", resp);
        localStorage.setItem("vonage_session_id", resp.sessionId);
        localStorage.setItem("vonage_token", resp.token);
        localStorage.setItem("vonage_cid", conversationData?.id);
        localStorage.setItem("vonage_meetingId", callId);
        localStorage.setItem("token", auth?.token);
        navigate("/connect");

        // setChatMessages(prev => [...prev, newMsg]);
        // setMessage('');
        // setSelectedFile(null);
        // scrollToBottom();
      });
    } catch (error) {
      showAlert({
        title: "Chat Screen Notification",
        message: error?.response?.data?.message ?? "Something went wrong",
        variant: "danger",
      });
    }
  };

  const joinCallButton = async (event) => {
    if (conversationData?.calls && conversationData?.calls[0]?.id) {
      // alert(conversationData?.calls && conversationData?.calls[0]?.id);
      meetingLinkOpen(event, conversationData?.calls[0]?.id);
      // setMeetingId(conversationData?.calls && conversationData?.calls[0]?.id);
    } else {
      const data = {
        conversationID: conversationData?.id,
      };
      try {
        const callRequestData = await createCall(data);
        // alert(callRequestData?.data && callRequestData?.data?.id)
        meetingLinkOpen(event, callRequestData?.data?.id);
        // setMeetingId(callRequestData?.data && callRequestData?.data?.id);
      } catch (error) {
        showAlert({
          title: "Chat Notification",
          message: error?.response?.data?.message ?? "something went wrong",
          variant: "danger",
        });
      }
    }
  };
  const createOrCheckNewCall = async () => {
    if (false && conversationData?.calls && conversationData?.calls[0]?.id) {
      // alert("avail")
      setMeetingId(conversationData?.calls && conversationData?.calls[0]?.id);
    } else {
      const data = {
        conversationID: conversationData?.id,
      };
      try {
        const callRequestData = await createCall(data);
        setMeetingId(callRequestData?.data && callRequestData?.data?.id);
      } catch (error) {
        showAlert({
          title: "Chat Notification",
          message: error?.response?.data?.message ?? "something went wrong",
          variant: "danger",
        });
      }
    }
  };

  const getFiles = async () => {
    try {
      // if (isLoadMore) setIsLoadingMore(true);

      const data = await getChatFiles(conversationData?.id, filesPagination);
      const newFiles = data?.data?.items || [];

      if (newFiles.length > 0) {
        newFiles.sort(
          (a, b) => new Date(a.created_date) - new Date(b.created_date)
        );
      }
      setChatFiles(newFiles);

      if (data?.data?.nextToken) {
        setFilesPagination((prev) => ({
          ...prev,
          nextToken: data.data.nextToken,
        }));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      showAlert({
        title: "Chat Screen Notification",
        message: error?.response?.data?.message ?? "Something went wrong",
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    // alert(activeTab)
  }, []);

  const handleCheckboxChange = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );

    console.log(
      "selectChange",
      selectedFiles.includes(fileId)
        ? selectedFiles.filter((id) => id !== fileId)
        : [...selectedFiles, fileId]
    );
  };

  const handleTabChange = (value) => {
    setActiveTab((prev) => value);
  };

  const handleDownloadAllFiles = () => {
    let type;
    if (activeTab == "docs") {
      type = [6, 7];
    } else if (activeTab == "images") {
      type = [3];
    } else if (activeTab == "videos") {
      type = [4, 8];
    }
    const filesToDownload = chatFiles
      ?.filter((e) => type.includes(e.message_type))
      ?.map((e) => e.message);
    console.log("filesToDownload", filesToDownload);
    downloadAll(filesToDownload, setProgress, conversationData?.id);
  };

  const handleSelectAllFiles = () => {
    let type;
    if (activeTab == "docs") {
      type = [5, 6, 7];
    } else if (activeTab == "images") {
      type = [3];
    } else if (activeTab == "videos") {
      type = [4];
    }
    const filesToDownload = chatFiles
      ?.filter((e) => type.includes(e.message_type))
      ?.map((e) => e.message);
    console.log("filesToDownload", filesToDownload);
    setSelectedFiles(filesToDownload);
  };

  const handleDownloadSelectedFiles = () => {
    console.log("filesToDownload", selectedFiles);
    downloadAll(selectedFiles, setProgress, conversationData?.id);
  };

  const cancelChatRequest = async (id, action) => {
    const data = {
      action: action,
    };
    try {
      const callRequestData = await endConversationRequest(id, data);
      setChatScreenFilter({ ...chatScreenFilter });
      setShowEndSessionModal(null);
      showAlert({
        title: "Chat Notification",
        message: callRequestData?.message ?? "something went wrong",
        variant: "success",
      });
    } catch (error) {
      showAlert({
        title: "Chat Notification",
        message: error?.response?.data?.message ?? "something went wrong",
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (chatType || !tabVisible) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [conversationData?.id, chatType, tabVisible]);

  let companyProfile;
  let companyName;
  if (conversationData?.conversationStatus === "Pending") {
    companyProfile = conversationData?.admin?.profilePic;
    companyName = conversationData?.admin?.companyName;
  }

  let status = useUserStatus(anotherMember?.user_id)
  const tabItem = { Docs: "docs", Images: "images", Videos: "videos" };
  return (
    <>
      <div className="chat-header-top bg-white border-bottom border-grey-200 px-4 py-3 d-flex align-items-center justify-content-between gap-4">
        <div
          className="d-flex align-items-center cursor-pointer gap-2"
          onClick={anotherMember ? () => setUserDetailModal(true) : undefined}
        >
          <ImagePreviewLightbox
            className="user-avtar user-avtar-40"
            src={
              anotherMember?.user?.profilePic || companyProfile ||
              "/images/user-circle.svg"
            }
          >
            <Image
              className="rounded-pill object-fit-cover"
              src={
                anotherMember?.user?.profilePic || companyProfile ||
                "/images/user-circle.svg"
              }
              width={40}
              height={40}
              alt="Expert"
            />
          </ImagePreviewLightbox>
          <div className="d-flex flex-column align-items-start justify-content-between">
            <div className="fs-16 fw-semibold text-gray-700">
              {/* {conversationData?.conversationType?.includes("internal") ? (
                anotherMember?.user?.firstName ?? conversationData?.createdBy?.firstName +" "+
                anotherMember?.user?.lastName ?? conversationData?.createdBy?.lastName
              ):(
                conversationData?.createdBy?.firstName +" "+ conversationData?.createdBy?.lastName
              )}
               */}

              <>
                {anotherMember?.user?.firstName} {" "}
                {anotherMember?.user?.lastName}
                {conversationData?.conversationStatus === "Pending" && companyName}
              </>

            </div>
            <div className="fs-13 fw-normal text-status-available">
              {conversationData?.conversationStatus !== "Pending" ? <UserStatusText status={status ?? anotherUser?.status} /> : ""}
            </div>
          </div>
        </div>
        {conversationData?.conversationType?.includes("chat") ? (
          <div className="d-flex align-items-center gap-2">
            <Button
              disabled={conversationData?.conversationStatus === "Pending"}
              className="btn-md bg-blue-100 text-blue-600 border-transparent d-flex align-items-center gap-2"
              onClick={() => {
                createOrCheckNewCall();
              }}
            >
              <VideoCameraPlusIcon size={20} />
              Video Link
            </Button>
            {conversationData?.conversationStatus === "Pending" && (
              <Button
                className="btn-md bg-red-100 text-red-600 border-transparent d-flex align-items-center gap-2"
                onClick={() => setShowEndSessionModal(conversationData?.id)}
              >
                <SignOutIcon size={20} />
                Cancel
              </Button>
            )}
            {conversationData?.conversationStatus !== "Pending" && !conversationData?.conversationType?.includes("internal") && <Button
              className="btn-md bg-red-100 text-red-600 border-transparent d-flex align-items-center gap-2"
              onClick={() => setShowEndSessionModal(conversationData?.id)}
            >
              <SignOutIcon size={20} />
              End
            </Button>}
          </div>
        ) : (
          sessionTimeStatus.isCurrentSession && (
            <div className="d-flex align-items-center gap-2">
              <Button
                className="btn-md bg-blue-100 text-blue-600 border-transparent d-flex align-items-center gap-2"
                onClick={(event) => {
                  joinCallButton(event);
                }}
              >
                <VideoCameraIcon />
                Join Call
              </Button>
            </div>
          )
        )}
      </div>

      {/* End Session Popup */}
      <Modal
        className="info-modal"
        show={showEndSessionModal}
        onHide={() => setShowEndSessionModal(null)}
        centered
        backdrop="static"
        keyboard={false}
      >
        {conversationData?.conversationStatus == "Pending" ? <Modal.Body>
          <div className="text-center">
            <Image src="/images/x-badge-red.svg" alt="X Badge" />
            <h2 className="fs-30 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to cancel this chat request?
            </h2>

            <div className="d-flex align-items-center gap-3">
              <Button
                variant="primary"
                className="btn w-100 mt-5"
                onClick={() => cancelChatRequest(conversationData?.id, "cancel_support")}
              >
                Yes, Cancel It
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => setShowEndSessionModal(false)}
              >
                No, Not Yet!
              </Button>
            </div>
          </div>
        </Modal.Body> : <Modal.Body>
          <div className="text-center">
            <Image src="/images/signout-badge-red.svg" alt="Signout Badge" />
            <h2 className="fs-30 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to end this session?
            </h2>
            <p className="fs-20 text-gray-500 fw-normal mb-0">
              All your conversation will be lost forever.
            </p>

            <div className="d-flex align-items-center gap-3">
              <Button
                variant="primary"
                className="btn w-100 mt-5"
                onClick={() => cancelChatRequest(conversationData?.id, "end_support")}
              >
                End Session
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => setShowEndSessionModal(false)}
              >
                No, Not Yet!
              </Button>
            </div>
          </div>
        </Modal.Body>}
      </Modal>

      {/* User Details Popup */}
      <Modal
        className="user-detail-modal"
        size="xxl"
        show={userDetailModal}
        onHide={() => setUserDetailModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body className="h-100 p-0">
          <div className="h-100 d-flex flex-column flex-lg-row align-items-start justify-content-center gap-3">
            {/* Normal User Detail */}
            <div className="user-modal-session-info">
              <div className="border-bottom border-grey-200 p-3">
                <h2 className="fs-20 fw-semibold text-gray-700 mb-1">
                  Session Information
                </h2>
              </div>
              <div className="p-3">
                <div className="d-flex align-items-center cursor-pointer border-bottom pb-4 gap-2">
                  <div className="chat-avatar">
                    <Image
                      className="rounded-pill object-fit-cover"
                      src={
                        anotherMember?.user?.profilePic ||
                        "/images/company-icon.png"
                      }
                      width={64}
                      height={64}
                      alt="Expert"
                    />
                  </div>
                  <div className="d-flex flex-column align-items-start justify-content-between gap-1">
                    <div className="fs-18 fw-bold text-gray-700">
                      {anotherMember?.user?.firstName}{" "}
                      {anotherMember?.user?.lastName}
                    </div>
                    <UserStatusText status={status ?? anotherUser?.status} />
                  </div>
                </div>
                <div className="border-bottom px-3 py-4">
                  <h3 className="fs-14 fw-medium text-blue-800 mb-4">
                    Personal Details
                  </h3>
                  <div className="mb-3">
                    <p className="fs-14 text-gray-500 mb-2">Email</p>
                    <p className="fs-16 text-gray-700 mb-0">
                      {anotherMember?.user?.email}
                    </p>
                  </div>
                  <div className="mb-3">
                    <p className="fs-14 text-gray-500 mb-2">Phone</p>
                    <p className="fs-16 text-gray-700 mb-0">
                      +{anotherMember?.user?.countryCode}{" "}
                      {anotherMember?.user?.phoneNo}
                    </p>
                  </div>
                  <div className="mb-3">
                    <p className="fs-14 text-gray-500 mb-2">Address</p>
                    <p className="fs-16 text-gray-700 mb-0">
                      {getAddress(conversationData)}
                    </p>
                  </div>
                </div>
                <div className="border-bottom px-3 py-4">
                  <h4 className="text-blue-800 fs-7 fw-medium mb-4">
                    Session Details
                  </h4>

                  <div className="mb-3">
                    <span className="fs-14 text-gray-500 d-block mb-2">
                      Channel
                    </span>
                    <ul className="d-flex flex-wrap align-content-center gap-2 list-unstyled mb-0">
                      {conversationData?.conversationType?.includes("chat") ? (
                        <li className="session-type-badge d-flex align-items-center gap-2">
                          <Image
                            src="/images/chats-circle.svg"
                            width={20}
                            height={20}
                            alt="Chat Circle Icon"
                          />{" "}
                          Chat
                        </li>
                      ) : (
                        <li className="session-type-badge d-flex align-items-center gap-2">
                          <Image
                            src="/images/video-camera-blue.svg"
                            width={20}
                            height={20}
                            alt="Video Camera Blue Icon"
                          />{" "}
                          Video
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="mb-3">
                    <span className="fs-14 text-gray-500 d-block mb-2">
                      {/* Session Timeline */}
                      {conversationData?.conversationType?.includes("chat")
                        ? "Session Time"
                        : "Session Timeline"}
                    </span>
                    <p className="mb-0">
                      {formatDateTimeToString(
                        conversationData?.startTime ||
                        conversationData?.createdAt
                      )}
                      {conversationData?.endTime &&
                        " - " +
                        formatDateTimeToString(
                          conversationData?.endTime
                        )}{" "}
                      - In{" "}
                      {conversationData?.conversationType?.includes("chat")
                        ? "Session Chat"
                        : "Session Video"}
                    </p>
                  </div>

                  {conversationData?.startTime && conversationData?.endTime && (
                    <div className="mb-3">
                      <span className="fs-14 text-gray-500 d-block mb-2">
                        Total Session Duration
                      </span>
                      <p className="mb-0">
                        {getHourMinuteDiffString(
                          new Date(conversationData.endTime),
                          new Date(conversationData.startTime)
                        )}
                      </p>
                    </div>
                  )}
                </div>
                {conversationData?.expertise?.name && (
                  <div className="border-bottom px-3 py-4">
                    <h3 className="fs-14 fw-medium text-blue-800 mb-4">
                      Expertise Details
                    </h3>
                    <div className="mb-3">
                      <p className="fs-14 text-gray-500 mb-2">Expertise</p>
                      <p className="fs-16 text-gray-700 mb-0">
                        {conversationData?.expertise?.name}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Group Information */}
            {/* {editGroup && !addUser && (
              <div className="user-modal-session-info">
                <div className="flex-grow-1 d-flex flex-column">
                  <div className="border-bottom border-grey-200 p-4">
                    <h2 className="fs-20 fw-semibold text-gray-700 d-flex align-items-center gap-2 mb-1">
                      <Button
                        className="btn btn-sm d-flex align-items-center gap-1"
                        variant="outline-secondary"
                        onClick={() => setEditGroup(false)}
                      >
                        <Image
                          src="/images/caret-left.svg"
                          width={16}
                          height={16}
                          alt="Back Arrow"
                        />
                        Back
                      </Button>{" "}
                      Edit Group Information
                    </h2>
                  </div>
                  <div className="p-3">
                    <div className="p-3 d-flex align-items-center gap-4">
                      <Image
                        src="/images/users-three-rounded.svg"
                        width={64}
                        height={64}
                        alt="Users Three Icon"
                      />
                      <Button
                        className="btn btn-sm d-flex align-items-center gap-2"
                        variant="outline-secondary"
                      >
                        <EditIcon /> Edit Photo
                      </Button>
                    </div>

                    <Form.Group className="mb-4">
                      <Form.Label>Group Name</Form.Label>
                      <Form.Control
                        placeholder="Enter group name"
                        value="AssistChat Group"
                      />
                    </Form.Group>

                    <div className="user-modal-group-details border-top px-3 py-4">
                      <h3 className="fs-14 fw-medium text-blue-800 mb-4">
                        Group Details
                      </h3>

                      <div className="mb-4">
                        <p className="fs-14 text-gray-500 mb-2">Created By</p>
                        <p className="fs-16 text-gray-700 d-flex align-items-center gap-2">
                          <Image
                            className="rounded-pill object-fit-cover"
                            src="/images/user-avatar-1.png"
                            width={32}
                            height={32}
                            alt="User Avatar"
                          />
                          <span>Expert user</span>
                        </p>
                      </div>
                      <div className="mb-4">
                        <p className="fs-14 text-gray-500 mb-2">Create On</p>
                        <p className="fs-16 text-gray-700 d-flex align-items-center gap-2">
                          06-01-2025, 12:51 PM
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 border-top p-3">
                    <Button variant="primary" className="btn-md">
                      Update & Save
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="btn-md"
                      onClick={() => setEditGroup(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )} */}

            {/* Edit Group Information */}
            {/* {!editGroup && !addUser && !groupUserInfo && (
              <div className="user-modal-session-info">
                <div className="flex-grow-1 d-flex flex-column">
                  <div className="border-bottom border-grey-200 p-4">
                    <h2 className="fs-20 fw-semibold text-gray-700 mb-1">
                      Group Information
                    </h2>
                  </div>
                  <div className="p-3">
                    <div className="p-3 d-flex align-items-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          src="/images/users-three-rounded.svg"
                          width={48}
                          height={48}
                          alt="Users Three Icon"
                        />
                        <div>
                          <h3 className="fs-18 fw-bold text-gray-700 mb-2">
                            AssistChat Group
                          </h3>
                          <p className="fs-14 fw-medium text-gray-500 mb-0">
                            Created by Expert User
                          </p>
                          <p className="fs-14 fw-medium text-gray-500 mb-0">
                            06-01-2025, 12:51 PM
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="icon"
                        className="bg-white text-gray-700 rounded-3 border-grey-300"
                        onClick={() => setEditGroup(true)}
                      >
                        <EditIcon />
                      </Button>
                    </div>
                    <div className="user-modal-all-users border-top px-3 py-4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h3 className="fs-14 fw-medium text-blue-800 mb-0">
                          All Users
                        </h3>
                        <Button
                          variant="outline-secondary"
                          className="btn-sm d-flex align-items-center gap-1"
                          onClick={() => (
                            setAddUser(true), setEditGroup(false)
                          )}
                        >
                          <PlusIcon size={16} />
                          Add Users
                        </Button>
                      </div>

                      <div className="cursor-pointer border-bottom py-3">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex aling-items-center gap-3">
                            <div className="chat-avatar md">
                              <Image
                                className="rounded-pill object-fit-cover"
                                src="/images/user-avatar-1.png"
                                width={48}
                                height={48}
                                alt="Expert"
                              />
                              <span className="status available"></span>
                            </div>
                            <div>
                              <h4 className="fs-16 fw-semibold mb-2">
                                Expert User (You)
                              </h4>
                              <p className="text-status-available fs-14 fw-medium mb-0">
                                Available
                              </p>
                            </div>
                          </div>
                          <div className="text-end">
                            <span className="bg-blue-50 text-blue-600 border border-blue-200 fs-12 fw-semibold rounded-2 px-2 py-1">
                              Admin
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        className="cursor-pointer border-bottom py-3"
                        onClick={() => setGroupUseInfo(true)}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex aling-items-center gap-3">
                            <div className="chat-avatar md">
                              <Image
                                className="rounded-pill object-fit-cover"
                                src="/images/user-avatar-1.png"
                                width={48}
                                height={48}
                                alt="Expert"
                              />
                              <span className="status available"></span>
                            </div>
                            <div>
                              <h4 className="fs-16 fw-semibold mb-2">
                                Internal User
                              </h4>
                              <p className="text-status-busy fs-14 fw-medium mb-0">
                                Busy
                              </p>
                            </div>
                          </div>
                          <div className="text-end">
                            <span className="bg-blue-50 text-blue-600 border border-blue-200 fs-12 fw-semibold rounded-2 px-2 py-1 me-2">
                              Admin
                            </span>
                            <img
                              className="opacity-50"
                              src="/images/caret-right.svg"
                              width={20}
                              height={20}
                              alt="Arrow Right"
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        className="cursor-pointer border-bottom py-3"
                        onClick={() => setGroupUseInfo(true)}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex aling-items-center gap-3">
                            <div className="chat-avatar md">
                              <Image
                                className="rounded-pill object-fit-cover"
                                src="/images/user-avatar-1.png"
                                width={48}
                                height={48}
                                alt="Expert"
                              />
                              <span className="status out"></span>
                            </div>
                            <div>
                              <h4 className="fs-16 fw-semibold mb-2">
                                Internal User
                              </h4>
                              <p className="text-status-out fs-14 fw-medium mb-0">
                                Out of Office
                              </p>
                            </div>
                          </div>
                          <div className="text-end">
                            <img
                              className="opacity-50"
                              src="/images/caret-right.svg"
                              width={20}
                              height={20}
                              alt="Arrow Right"
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        className="cursor-pointer border-bottom py-3"
                        onClick={() => setGroupUseInfo(true)}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex aling-items-center gap-3">
                            <div className="chat-avatar md">
                              <Image
                                className="rounded-pill object-fit-cover"
                                src="/images/user-avatar-1.png"
                                width={48}
                                height={48}
                                alt="Expert"
                              />
                              <span className="status vacation"></span>
                            </div>
                            <div>
                              <h4 className="fs-16 fw-semibold mb-2">
                                Internal User
                              </h4>
                              <p className="text-status-vacation fs-14 fw-medium mb-0">
                                On Vacation
                              </p>
                            </div>
                          </div>
                          <div className="text-end">
                            <img
                              className="opacity-50"
                              src="/images/caret-right.svg"
                              width={20}
                              height={20}
                              alt="Arrow Right"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-top p-3">
                    <button
                      type="button"
                      className="btn bg-red-100 text-red-600 border-0 d-flex align-items-center gap-2"
                      onClick={() => (
                        setExitGroupModal(true), setUserDetailModal(false)
                      )}
                    >
                      <SignOutIcon /> Exit Group
                    </button>
                  </div>
                </div>
              </div>
            )} */}

            {/* Add User */}
            {/* {addUser && (
              <div className="user-modal-session-info">
                <div className="flex-grow-1 d-flex flex-column">
                  <div className="border-bottom border-grey-200 p-4">
                    <h2 className="fs-20 fw-semibold text-gray-700 d-flex align-items-center gap-2 mb-1">
                      <Button
                        className="btn btn-sm d-flex align-items-center gap-1"
                        variant="outline-secondary"
                        onClick={() => (setEditGroup(false), setAddUser(false))}
                      >
                        <Image
                          src="/images/caret-left.svg"
                          width={16}
                          height={16}
                          alt="Back Arrow"
                        />
                        Back
                      </Button>{" "}
                      Add Users
                    </h2>
                  </div>

                  <div className="chat-contact-list p-3">
                    <div className="mb-4">
                      <input
                        type="text"
                        className="form-control form-control-search bg-gray-100 border-0 chat-search"
                        placeholder="Search here..."
                      />
                    </div>
                    <div className="mb-4">
                      <h3 className="fs-16 fw-bold px-2 mb-2">A</h3>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-1" />
                        <label
                          htmlFor="user-1"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-2" />
                        <label
                          htmlFor="user-2"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-3" />
                        <label
                          htmlFor="user-3"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-3.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <h3 className="fs-16 fw-bold px-2 mb-2">B</h3>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-5" />
                        <label
                          htmlFor="user-5"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-4" />
                        <label
                          htmlFor="user-4"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <h3 className="fs-16 fw-bold px-2 mb-2">C</h3>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-5" />
                        <label
                          htmlFor="user-5"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                      <div className="chat-contact chat-contact-check d-flex align-items-center flex-row-reverse justify-content-between gap-3">
                        <input type="checkbox" name="a" id="user-4" />
                        <label
                          htmlFor="user-4"
                          className="chat-contact-label w-100 d-flex align-items-center justify-content-between border-bottom border-grey-200 px-2 py-3 gap-3"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <Image
                              className="rounded-pill"
                              src="/images/user-avatar-1.png"
                              width={48}
                              height={48}
                              alt="User Icon"
                            />
                            <p className="fs-16 fw-semibold text-gray-700 mb-0">
                              Internal User
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 border-top p-3">
                    <Button variant="primary" className="btn-md">
                      Add Users (2 selected)
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="btn-md"
                      onClick={() => (setEditGroup(false), setAddUser(false))}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )} */}

            {/* Group User Information */}
            {groupUserInfo && !editGroup && !addUser && (
              <div className="user-modal-session-info">
                <div className="border-bottom border-grey-200 p-4">
                  <h2 className="fs-20 fw-semibold text-gray-700 d-flex align-items-center gap-2 mb-1">
                    <Button
                      className="btn btn-sm d-flex align-items-center gap-1"
                      variant="outline-secondary"
                      onClick={() => (
                        setEditGroup(false),
                        setAddUser(false),
                        setGroupUseInfo(false)
                      )}
                    >
                      <Image
                        src="/images/caret-left.svg"
                        width={16}
                        height={16}
                        alt="Back Arrow"
                      />
                      Back
                    </Button>{" "}
                    User Information
                  </h2>
                </div>
                <div className="user-modal-session-info-details p-3">
                  <div className="d-flex align-items-center p-3 pt-1 gap-3">
                    <div className="chat-avatar md">
                      <Image
                        className="rounded-pill object-fit-cover"
                        src="/images/user-avatar-1.png"
                        width={48}
                        height={48}
                        alt="Expert"
                      />
                      <span className="status available"></span>
                    </div>
                    <div>
                      <div className="fs-18 fw-bold text-gray-700 mb-1">
                        Expert Name
                      </div>
                      <p className="text-status-available fs-14 fw-medium mb-0">
                        Available
                      </p>
                    </div>
                  </div>
                  <div className="border-top p-3">
                    <h3 className="fs-14 fw-medium text-blue-800 mb-4">
                      Personal Details
                    </h3>
                    <div className="mb-3">
                      <p className="fs-14 fw-normal text-gray-500 mb-2">
                        Email
                      </p>
                      <p className="fs-14 fw-normal text-gray-700 mb-0">
                        internal_user@email.com
                      </p>
                    </div>
                    <div className="mb-3">
                      <p className="fs-14 fw-normal text-gray-500 mb-2">
                        Phone
                      </p>
                      <p className="fs-14 fw-normal text-gray-700 mb-0">
                        +1 (316) 922-4816
                      </p>
                    </div>
                    <div className="mb-3">
                      <p className="fs-14 fw-normal text-gray-500 mb-2">
                        Address
                      </p>
                      <p className="fs-14 fw-normal text-gray-700 mb-0">
                        1092 Bakery St, Tampa, Florida, USA - 09834
                      </p>
                    </div>
                  </div>
                  <div className="border-top p-3">
                    <h3 className="fs-14 fw-medium text-blue-800 mb-4">
                      Expertise
                    </h3>
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          className="rounded-pill"
                          src="/images/product-icon.png"
                          width={48}
                          height={48}
                          alt="User Icon"
                        />
                        <div>
                          <p className="fs-16 fw-semibold text-gray-700 mb-1">
                            Product Name
                          </p>
                          <p className="fs-14 fw-normal text-gray-500 mb-0">
                            Brand Name
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          className="rounded-pill"
                          src="/images/product-icon.png"
                          width={48}
                          height={48}
                          alt="User Icon"
                        />
                        <div>
                          <p className="fs-16 fw-semibold text-gray-700 mb-1">
                            Product Name
                          </p>
                          <p className="fs-14 fw-normal text-gray-500 mb-0">
                            Brand Name
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          className="rounded-pill"
                          src="/images/product-icon.png"
                          width={48}
                          height={48}
                          alt="User Icon"
                        />
                        <div>
                          <p className="fs-16 fw-semibold text-gray-700 mb-1">
                            Product Name
                          </p>
                          <p className="fs-14 fw-normal text-gray-500 mb-0">
                            Brand Name
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          className="rounded-pill"
                          src="/images/product-icon.png"
                          width={48}
                          height={48}
                          alt="User Icon"
                        />
                        <div>
                          <p className="fs-16 fw-semibold text-gray-700 mb-1">
                            Product Name
                          </p>
                          <p className="fs-14 fw-normal text-gray-500 mb-0">
                            Brand Name
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-3">
                        <Image
                          className="rounded-pill"
                          src="/images/product-icon.png"
                          width={48}
                          height={48}
                          alt="User Icon"
                        />
                        <div>
                          <p className="fs-16 fw-semibold text-gray-700 mb-1">
                            Product Name
                          </p>
                          <p className="fs-14 fw-normal text-gray-500 mb-0">
                            Brand Name
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 border-top p-3">
                  <Button
                    variant="primary"
                    className="btn-md"
                    onClick={() => (
                      setMakeAdminModal(true), setUserDetailModal(false)
                    )}
                  >
                    Make Admin
                  </Button>
                  {/* <Button variant="" className="btn-md bg-red-100 text-red-600">
                    Dismiss As Admin
                  </Button> */}
                  <Button
                    variant=""
                    className="btn-md bg-red-100 text-red-600"
                    onClick={() => (
                      setRemoveUserModal(true), setUserDetailModal(false)
                    )}
                  >
                    Remove User
                  </Button>
                </div>
              </div>
            )}

            <div className="user-modal-session-details">
              <div className="d-flex align-items-center justify-content-between gap-4 border-bottom border-grey-200 px-4 py-3">
                <Tabs
                  tabItem={tabItem}
                  activeTab={activeTab}
                  changeFunction={handleTabChange}
                  changeValue={null}
                />
                <Button
                  as="button"
                  variant="primary"
                  onClick={handleDownloadAllFiles}
                  className="btn-sm d-flex align-items-center gap-2"
                >
                  <Image
                    src="/images/download-white.svg"
                    width={18}
                    height={18}
                    alt="Download White Icon"
                  />
                  Download All
                </Button>
                {/* {progress && <div>{progress}</div>} */}
              </div>
              {selectedFiles.length > 0 && (
                <div className="bg-blue-100 p-3 d-md-flex align-items-md-center justify-content-md-between">
                  <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
                    <Button
                      as="button"
                      onClick={handleSelectAllFiles}
                      variant="white"
                      className="btn-md bg-white border-0 shadow-none text-blue"
                    >
                      Select All
                    </Button>
                    <p className="fs-18 fw-semibold mb-0">
                      {selectedFiles.length}{" "}
                      {selectedFiles.length > 1 ? "items" : "item"} selected
                    </p>
                  </div>

                  <Button
                    as="button"
                    variant="white"
                    onClick={handleDownloadSelectedFiles}
                    className="btn-md bg-white border-0 shadow-none text-blue d-flex align-items-center gap-2"
                  >
                    <DownloadIcon />
                    Download Selected
                  </Button>
                </div>
              )}
              <div className="p-3" hidden={activeTab !== "docs"}>
                {chatFiles
                  ?.filter((e) => [6, 7].includes(e.message_type))
                  .map((element, i) => {
                    const parts = element?.message.split("/");
                    const filename = parts[parts.length - 1];
                    const isChecked = selectedFiles.some(
                      (file) => file === element.message
                    ); // assuming `element.id` is unique

                    return (
                      <div className="border-bottom d-flex align-items-center gap-3 px-3 py-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={element?.id}
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(element.message)}
                        />
                        <label
                          htmlFor={element?.i}
                          className="d-flex align-items-center gap-3 cursor-pointer"
                        >
                          <Image
                            src={"/images/file-type-icon-doc.svg"}
                            width={48}
                            height={48}
                            alt="Icon"
                          />
                          <div>
                            <h3 className="fs-14 fw-medium text-gray-700 mb-2">
                              {filename}
                            </h3>
                            <p className="fs-12 text-gray-500 mb-0">
                              Uploaded on{" "}
                              {formatDate(new Date(element?.created_date))}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })}
              </div>
              <div className="p-3" hidden={activeTab !== "images"}>
                <Row className="g-3">
                  {chatFiles
                    ?.filter((e) => e.message_type === 3)
                    .map((element) => {
                      const parts = element?.message.split("/");
                      const filename = parts[parts.length - 1];
                      const isChecked = selectedFiles.some(
                        (file) => file === element.message
                      ); // assuming `element.id` is unique

                      return (
                        <Col md="3">
                          <div className="d-flex align-items-start gap-2 border p-2 rounded-3 h-100">
                            <label htmlFor="image-1" className="cursor-pointer">
                              <input
                                type="checkbox"
                                name={filename}
                                onClick={(e) => console.log(e.target.checked)}
                                className="form-check-input"
                                id="image-1"
                                checked={isChecked}
                                onChange={() =>
                                  handleCheckboxChange(element.message)
                                }
                              />
                            </label>
                            <div>
                              <ImagePreviewLightbox
                                className="ratio ratio-1x1"
                                src={
                                  element.message ||
                                  "/images/image-placeholder.jpg"
                                }
                              >
                                <Image
                                  className="bg-gray-100 object-fit-contain rounded-1"
                                  src={
                                    element?.message ||
                                    "/images/image-placeholder.jpg"
                                  }
                                  // src="/images/image-placeholder.jpg"
                                  alt={filename}
                                />
                              </ImagePreviewLightbox>
                              <p className="fs-14 fw-medium text-gray-700 word-break mt-2 mb-1">
                                {filename}
                              </p>
                              <p className="fs-14 fw-nornal text-gray-500 mb-0">
                                {formatDate(new Date(element?.created_date))}
                              </p>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                </Row>
              </div>
              <div className="p-3" hidden={activeTab !== "videos"}>
                <Row className="g-3">
                  {chatFiles
                    ?.filter((e) => [4, 8].includes(e.message_type))
                    .map((element) => {
                      const parts = element?.message.split("/");
                      const filename = parts[parts.length - 1];
                      const isChecked = selectedFiles.some(
                        (file) => file.message === element.message
                      ); // assuming `element.id` is unique

                      return (
                        <Col md="3">
                          <div className="d-flex align-items-start gap-2 border p-2 rounded-3 h-100">
                            <label htmlFor="image-1" className="cursor-pointer">
                              <input
                                type="checkbox"
                                name={filename}
                                onClick={(e) => console.log(e.target.checked)}
                                className="form-check-input"
                                id="image-1"
                                checked={isChecked}
                                onChange={() =>
                                  handleCheckboxChange(element.message)
                                }
                              />
                            </label>
                            <div>
                              <div className="position-relative">
                                <Image
                                  className="position-absolute top-50 start-50 translate-middle pe-none z-1"
                                  src="images/play-rounded.svg"
                                  width={24}
                                  height={24}
                                  alt="Play Icon"
                                />
                                <VideoPopupLightbox
                                  className="ratio ratio-1x1"
                                  videoSlide={{
                                    type: "video",
                                    sources: [
                                      {
                                        src: element?.message,
                                        type: "video/mp4",
                                      },
                                    ],
                                  }}
                                >
                                  <video
                                    className="bg-gray-100 object-fit-contain rounded-1"
                                    muted
                                  >
                                    <source
                                      src={element?.message}
                                      type="video/mp4"
                                      allowFullScreen
                                    />
                                  </video>
                                </VideoPopupLightbox>
                              </div>
                              <p className="fs-14 fw-medium text-gray-700 word-break mt-2 mb-1">
                                {filename}
                              </p>
                              <p className="fs-14 fw-nornal text-gray-500 mb-0">
                                {formatDate(new Date(element?.created_date))}
                              </p>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                </Row>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={() => setUserDetailModal(false)}
            ></button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Exit Group Modal */}
      <Modal
        className="info-modal"
        show={exitGroupModal}
        onHide={() => setExitGroupModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <div className="text-center">
            <Image src="/images/signout-badge-red.svg" alt="Signout Badge" />
            <h2 className="fs-30 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to exit this AssistChat group?
            </h2>
            <p className="fs-20 text-gray-500 mb-0">
              All your conversation will be lost forever. You’ll not be able to
              join again.
            </p>

            <div className="d-flex align-items-center gap-3">
              <Button variant="primary" className="btn w-100 mt-5">
                Yes, Exit Group
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => setExitGroupModal(false)}
              >
                No, Not Yet!
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Make Admin Modal */}
      <Modal
        className="info-modal"
        show={makeAdminModal}
        onHide={() => setMakeAdminModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <div className="text-center">
            <Image
              src="/images/user-settings-badge-blue.svg"
              alt="User Settings Badge"
            />
            <h2 className="fs-30 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to make admin to this user?
            </h2>
            <p className="fs-20 text-gray-500 mb-0">
              The user will gain all the access of admin role.
            </p>

            <div className="d-flex align-items-center gap-3">
              <Button variant="primary" className="btn w-100 mt-5">
                Yes, Make Admin
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => (
                  setMakeAdminModal(false), setUserDetailModal(true)
                )}
              >
                No, Not Yet!
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Remove User Modal */}
      <Modal
        className="info-modal"
        show={removeUserModal}
        onHide={() => setRemoveUserModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Body>
          <div className="text-center">
            <Image
              src="/images/user-minus-badge-red.svg"
              alt="User Minus Badge"
            />
            <h2 className="fs-30 fw-semibold text-gray-700 mt-5 mb-2">
              Are you sure you want to remove this user from the group?
            </h2>
            <p className="fs-20 text-gray-500 mb-0">
              You can still be able to add the user later on.
            </p>

            <div className="d-flex align-items-center gap-3">
              <Button variant="primary" className="btn w-100 mt-5">
                Yes, Remove User
              </Button>
              <Button
                variant="gray"
                className="btn w-100 mt-5"
                onClick={() => (
                  setRemoveUserModal(false), setUserDetailModal(true)
                )}
              >
                No, Not Yet!
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ChatHeader;
